/**
 * Byte accounting for a multi-file chunked upload.
 *
 * Kept out of api-client.ts on purpose: the transport there is already long,
 * and this is arithmetic with its own edge cases (retries, a fallback that
 * re-sends bytes already sent) that deserves to be tested without a network.
 */

/**
 * Which leg of a submit is running. The upload dialog stays open across all of
 * them: before that, the user pressed a button and watched nothing happen for
 * minutes, which is indistinguishable from a hang.
 */
export type UploadPhase = 'preparing' | 'uploading' | 'finalizing'

/**
 * Per-file transport state. `retrying` is NOT a failure: a chunk went to the
 * direct-to-storage leg and did not land, so the same bytes are going again (or
 * through the proxy). The upload has not failed and must not be dressed as
 * though it had.
 */
export type UploadFileState = 'uploading' | 'retrying' | 'done'

/** The three hooks a caller passes to watch a submit without owning its transport. */
export interface UploadProgressCallbacks {
  onPhase?: (phase: UploadPhase) => void
  /** Byte-accurate progress, overall and per file. Chunked uploads only. */
  onUploadProgress?: (snapshot: UploadProgressSnapshot) => void
  onFileState?: (fileIndex: number, state: UploadFileState) => void
}

export interface UploadProgressSnapshot {
  /** 0..1 across every byte of every file. */
  overall: number
  /** 0..1 per file, index-aligned with the sizes the ledger was built from. */
  files: number[]
}

export interface UploadProgressLedger {
  /** Report bytes in flight for one part. `fraction` is that part's own 0..1. */
  onPartProgress(fileIndex: number, partNumber: number, fraction: number): UploadProgressSnapshot
  /** Mark one part fully uploaded. Idempotent — a retried part is not counted twice. */
  onPartComplete(fileIndex: number, partNumber: number): UploadProgressSnapshot
  /** Mark every remaining byte of a file done (its multipart upload committed). */
  onFileComplete(fileIndex: number): UploadProgressSnapshot
  snapshot(): UploadProgressSnapshot
}

export function createUploadProgressLedger(
  fileSizes: number[],
  chunkSizeBytes: number
): UploadProgressLedger {
  const totalBytes = fileSizes.reduce((sum, size) => sum + Math.max(0, size), 0)
  const completedParts = new Set<string>()
  const completedBytes = fileSizes.map(() => 0)
  const inFlightBytes = new Map<string, number>()
  // Displayed fractions never move backwards. They otherwise would: when the
  // direct-to-storage leg fails, the same part is re-sent through the proxy and
  // its in-flight bytes restart at zero. The bytes really were re-sent — but a
  // bar that jumps back tells the user their upload was lost, which it was not.
  const fileFloor = fileSizes.map(() => 0)
  let overallFloor = 0

  const key = (fileIndex: number, partNumber: number) => `${fileIndex}:${partNumber}`

  const partSize = (fileIndex: number, partNumber: number): number => {
    const size = fileSizes[fileIndex] ?? 0
    if (chunkSizeBytes <= 0) return size
    const start = (partNumber - 1) * chunkSizeBytes
    return Math.max(0, Math.min(chunkSizeBytes, size - start))
  }

  const snapshot = (): UploadProgressSnapshot => {
    const pending = new Map<number, number>()
    for (const [mapKey, bytes] of inFlightBytes) {
      const fileIndex = Number(mapKey.split(':')[0])
      pending.set(fileIndex, (pending.get(fileIndex) ?? 0) + bytes)
    }

    let sentBytes = 0
    const files = fileSizes.map((size, index) => {
      const sent = Math.min(size, completedBytes[index] + (pending.get(index) ?? 0))
      sentBytes += sent
      const fraction = size <= 0 ? 1 : sent / size
      fileFloor[index] = Math.max(fileFloor[index], clampUnit(fraction))
      return fileFloor[index]
    })

    const overall = totalBytes <= 0 ? 0 : clampUnit(sentBytes / totalBytes)
    overallFloor = Math.max(overallFloor, overall)
    return { overall: overallFloor, files }
  }

  return {
    onPartProgress(fileIndex, partNumber, fraction) {
      const mapKey = key(fileIndex, partNumber)
      // A completed part is already counted in `completedBytes`; letting a late
      // progress event add it again would push the file past 100%.
      if (!completedParts.has(mapKey)) {
        inFlightBytes.set(mapKey, clampUnit(fraction) * partSize(fileIndex, partNumber))
      }
      return snapshot()
    },
    onPartComplete(fileIndex, partNumber) {
      const mapKey = key(fileIndex, partNumber)
      inFlightBytes.delete(mapKey)
      if (!completedParts.has(mapKey)) {
        completedParts.add(mapKey)
        completedBytes[fileIndex] += partSize(fileIndex, partNumber)
      }
      return snapshot()
    },
    onFileComplete(fileIndex) {
      for (const mapKey of [...inFlightBytes.keys()]) {
        if (Number(mapKey.split(':')[0]) === fileIndex) inFlightBytes.delete(mapKey)
      }
      completedBytes[fileIndex] = fileSizes[fileIndex] ?? 0
      return snapshot()
    },
    snapshot,
  }
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(1, Math.max(0, value))
}
